import { TripStatus } from '@/features/trips/TripStatus';
import { BookingNoShowResponseDto, BookingNoShowType } from '@/features/trips/BookingNoShowModels';
import { getPassingTimeTotals, getPassingTimeTotalsOnboardForStop } from '@/features/trips/PassingTimeTotalCalculator';
import { Departure } from '@/features/departures/Departure';
import { PassingTime } from '@/features/trips/PassingTime';
import { ServiceJourney } from '@/features/departures/ServiceJourney';
import { TransportType } from '@/features/shared/models/TransportType';
import { Trip } from '@/features/trips/Trip';
import _ from 'lodash';
import { defineStore } from 'pinia';
import { useBaseStore } from '../shared/baseStore';
import { container } from 'inversify-props';
import { ITripService, ITripServiceId } from '@/features/trips/ITripService';
import { EntryMode } from '@/features/shared/models/EntryMode';
import { Events, useEmitter } from '@/plugins/emitter';
import { createPassingTimeCoalescer } from '@/features/trips/PassingTimeCoalescer';

let passingTimeCoalescer: ReturnType<typeof createPassingTimeCoalescer> | null = null;

function getPassingTimeCoalescer() {
    if (passingTimeCoalescer === null) {
        passingTimeCoalescer = createPassingTimeCoalescer(({ trip, passingTime, timeStamp }) => {
            const tripsService = container.get<ITripService>(ITripServiceId);
            return tripsService.updatePassingTime(trip, passingTime, timeStamp);
        });
    }
    return passingTimeCoalescer;
}

export const useTripsStore = defineStore('trips', {
    state: () => ({
        trips: new Array<Trip>(),
        departures: new Array<Departure>(),
        transportType: TransportType.None,
        singleViewMode: false,
        tripsUpdatedAt: null as Date | null,
        bookingNoShowList: new Array<BookingNoShowResponseDto>(),
        bookingNoShowPaging: {
            pageNumber: 1,
            pageSize: 100,
            totalItemCount: 0,
            pageCount: 1
        },
        searchFilters: {
            noShowType: null,
            dateFrom: null,
            dateTo: null,
            tripId: null,
            operatingDay: null
        }
    }),
    actions: {
        setTrip(payload: Trip) {
            const index = this.trips.findIndex(x => x.tripId === payload.tripId);
            this.trips[index] = payload;
            this.trips = this.enrichTrips(this.trips);
            this.departures = this.createDepartures();
        },
        async fetchTrips({
            lineNumber,
            calendarDay,
            backgroundSync
        }: {
            lineNumber: string;
            calendarDay: string;
            backgroundSync: boolean;
        }) {
            const baseStore = useBaseStore();
            const tripsService = container.get<ITripService>(ITripServiceId);

            if (!baseStore.isOnline) return;

            try {
                if (!backgroundSync) {
                    baseStore.setIsLoading(true);
                }

                const response = await tripsService.getTrips(lineNumber, calendarDay);
                this.trips = this.enrichTrips(response.trips);
                this.singleViewMode = response.singleViewMode;
                this.transportType = response.transportType;
                this.departures = this.createDepartures();

                // set entryMode to MANUAL for ferries
                let entryMode = EntryMode.CLICKER;
                if (response.transportType === TransportType.Water) entryMode = EntryMode.MANUAL;
                baseStore.entryMode = entryMode;

                this.tripsUpdatedAt = new Date();

                if (!backgroundSync) {
                    baseStore.setIsLoading(false);
                }
            } catch (e) {
                console.error(e);
                baseStore.setIsLoading(false);
            }
        },
        async initMpcTrip({ trip, calendarDay, isReadOnly }: { trip: Trip; calendarDay: string; isReadOnly: boolean }) {
            const baseStore = useBaseStore();
            const tripsService = container.get<ITripService>(ITripServiceId);
            const eventbus = useEmitter();

            if (!trip.hasMpcTrip && !isReadOnly) {
                baseStore.setIsLoading(true);

                await tripsService.createTrip(trip.tripId, calendarDay).catch(error => {
                    if (error.message === 'createTripFailed') {
                        eventbus.emitter.emit(Events.goBack);
                        eventbus.emitter.emit(Events.notification, error.message);
                    }
                });
                await this.fetchTrips({
                    lineNumber: trip.lineNumber,
                    calendarDay,
                    backgroundSync: true
                });

                baseStore.setIsLoading(false);
            }
        },
        // NOTE: the returned promise resolves once the tap is recorded and the send is
        // SCHEDULED (debounced ~300 ms) — not when the POST completes. Callers that
        // navigate away immediately must rely on the page-level flush triggers.
        async addOrUpdatePassingTime({ trip, passingTime }: { trip: Trip; passingTime: PassingTime }) {
            // Capture tap time before any async work so the event carries the true
            // tap-order timestamp, even when the request is queued offline (KNH-307).
            const tapTime = Date.now();

            if (!trip.lineNumber || !trip.tripId) {
                throw new Error('BadRequestError');
            }

            const quayRef = passingTime.stop.quayRef;
            const time = passingTime.passingTime;

            const index = trip.passingTimes.findIndex(x => x.stop.quayRef === quayRef && x.passingTime == time);
            trip.passingTimes[index] = passingTime;

            trip.tripStatus = TripStatus.Active;

            // update state (optimistic UI, every tap)
            this.setTrip(trip);

            // Snapshot the counts now: stopEventData inner objects are mutated in place
            // elsewhere (e.g. cancelTrip zeroes them), which would otherwise race the
            // debounced send and ship wrong values.
            const snapshot: PassingTime = {
                ...passingTime,
                stopEventData: Object.fromEntries(
                    Object.entries(passingTime.stopEventData).map(([key, value]) => [key, { ...value }])
                ) as PassingTime['stopEventData']
            };

            // debounce the network send per stop; final snapshot wins
            getPassingTimeCoalescer().schedule({ trip, passingTime: snapshot, timeStamp: tapTime });
        },
        flushPassingTimes() {
            getPassingTimeCoalescer().flush();
        },
        async updateVesselName({ trip, vesselName }: { trip: Trip; vesselName: string }) {
            const tripsService = container.get<ITripService>(ITripServiceId);
            trip.vesselName = vesselName;
            trip.tripStatus = TripStatus.Active;
            this.setTrip(trip);
            await tripsService.updateTrip(trip);
        },
        async startExistingTrip({ trip }: { trip: Trip }) {
            const tripsService = container.get<ITripService>(ITripServiceId);
            trip.tripStatus = TripStatus.Active;
            this.setTrip(trip);
            await tripsService.updateTrip(trip);
        },
        async completeTrip(trip: Trip) {
            this.flushPassingTimes();
            const tripsService = container.get<ITripService>(ITripServiceId);
            trip.tripStatus = TripStatus.Finished;
            this.setTrip(trip);
            await tripsService.completeTrip(trip.tripId, trip.operatingDay);
        },
        async cancelTrip(trip: Trip) {
            this.flushPassingTimes();
            const tripsService = container.get<ITripService>(ITripServiceId);
            trip.tripStatus = TripStatus.NotStarted;
            trip.passingTimes.forEach(passingTIme => {
                for (const key in passingTIme.stopEventData) {
                    passingTIme.stopEventData[key].in = 0;
                    passingTIme.stopEventData[key].out = 0;
                }
            });
            this.setTrip(trip);
            await tripsService.cancelTrip(trip.tripId, trip.operatingDay);
        },
        enrichTrips(trips: Trip[]) {
            return trips.map(t => {
                if (!t.autoSwitchTripsEnabled) return t;

                const secondLast = t.passingTimes[t.passingTimes.length - 2];
                const secondLastTotals = getPassingTimeTotalsOnboardForStop(t.passingTimes, secondLast);
                const last = t.passingTimes[t.passingTimes.length - 1];

                last.stopEventData = Object.keys(last.stopEventData).reduce((acc, curr) => {
                    acc[curr] = {
                        in: 0,
                        out: secondLastTotals[curr]
                    };

                    return acc;
                }, {});

                return t;
            });
        },
        createDepartures() {
            const getPassengerTotals = (passingTimes: PassingTime[], tripStatus: TripStatus) => {
                if (tripStatus === TripStatus.Unknown || tripStatus === TripStatus.None || tripStatus === TripStatus.NotStarted) {
                    return null;
                }
                return getPassingTimeTotals(passingTimes).passengers;
            };

            const getCarTotals = (passingTimes: PassingTime[], tripStatus: TripStatus) => {
                if (tripStatus === TripStatus.Unknown || tripStatus === TripStatus.None || tripStatus === TripStatus.NotStarted) {
                    return null;
                }
                return getPassingTimeTotals(passingTimes).allCars;
            };

            return _(this.trips)
                .groupBy((trip: Trip) => trip.originQuayName + trip.destination)
                .map(
                    (trips: Trip[]): Departure => ({
                        destination: trips[0].destination,
                        originQuayName: trips[0].originQuayName,
                        serviceJourneys: trips.map(
                            (trip: Trip): ServiceJourney => ({
                                id: 0,
                                tripId: trip.tripId,
                                serviceJourneyForDay: {
                                    originDeparture: trip.originDeparture,
                                    isOnDuty: trip.isOnDuty
                                },
                                totalPassengers: getPassengerTotals(trip.passingTimes, trip.tripStatus),
                                totalCars: getCarTotals(trip.passingTimes, trip.tripStatus),
                                riderTypes: trip.riderTypes,
                                vesselName: trip.vesselName,
                                hasStarted: trip.tripStatus === TripStatus.Active,
                                bookedStopCount: _.sum(trip.passingTimes.map(x => (x.bookingCount > 0 ? 1 : 0)))
                            })
                        ),
                        singleViewMode: this.singleViewMode,
                        transportType: this.transportType
                    })
                )
                .filter((departure: Departure) => departure.serviceJourneys.length > 0)
                .orderBy([
                    (departure: Departure) => departure.serviceJourneys[0].serviceJourneyForDay.originDeparture,
                    'destination',
                    'originQuayName'
                ])
                .value();
        },
        async registerNoShow(trip: Trip, passingTime: PassingTime, status: BookingNoShowType, calendarDay: string) {
            const tripsService = container.get<ITripService>(ITripServiceId);

            if (!trip.lineNumber || !trip.tripId) {
                throw new Error('BadRequestError');
            }

            const noshowData = {
                calendarDay: calendarDay,
                tripId: trip.tripId,
                tripStop: passingTime,
                bookingCount: passingTime.bookingCount,
                bookingNoShowType: status,
                operatingDay: trip.operatingDay
            };

            const bookingNoShow: BookingNoShowResponseDto = {
                id: 0,
                tripId: trip.tripId,
                operatingDay: trip.operatingDay,
                tripStop: passingTime,
                bookingNoShowType: status,
                bookingCount: passingTime.bookingCount,
                departure: passingTime.stopName,
                destination: trip.destination
            };

            this.bookingNoShowList = [bookingNoShow, ...this.bookingNoShowList];

            await tripsService.registerBookingNoShow(noshowData);
        },
        async deleteNoShow(bookingNoShow: BookingNoShowResponseDto) {
            const tripsService = container.get<ITripService>(ITripServiceId);

            // Remove locally by composite identity
            this.bookingNoShowList = this.bookingNoShowList.filter(x => {
                const sameTrip = x.tripId === bookingNoShow.tripId;
                const sameStop =
                    x.tripStop?.stop?.quayRef === bookingNoShow.tripStop?.stop?.quayRef &&
                    x.tripStop?.stop?.publicCode === bookingNoShow.tripStop?.stop?.publicCode &&
                    x.tripStop?.sequenceOrder === bookingNoShow.tripStop?.sequenceOrder;
                const sameOpDay = x.operatingDay === bookingNoShow.operatingDay;

                const isSame = sameTrip && sameStop && sameOpDay;
                return !isSame;
            });

            // Always call backend delete by composite key (no id dependency)
            await tripsService.deleteBookingNoShow({
                tripId: bookingNoShow.tripId,
                operatingDay: bookingNoShow.operatingDay,
                tripStop: bookingNoShow.tripStop
            });
        },
        async loadBookingNoShows(page?: number) {
            const tripsService = container.get<ITripService>(ITripServiceId);
            const pageNumber = page ? page : this.bookingNoShowPaging.pageNumber;
            const response = await tripsService.getBookingNoShowList(
                { page: pageNumber, size: this.bookingNoShowPaging.pageSize },
                this.searchFilters
            );

            this.bookingNoShowList = response.data;
            this.bookingNoShowPaging = response.paging;
        }
    }
});
